import { t } from 'i18next'
import React from 'react'
import TextInput from '../../components/Form/Inputs/TextInput'
import { CandidateFormValues } from '../../store/types/form/CandidateFormValues'
import { Control } from 'react-hook-form'
import { Typography } from '@mui/material'
import { PhoneNumberUtil } from 'google-libphonenumber'
import SelectInput, { Option } from '../../components/Form/Inputs/SelectInput'
import { WorkingMethod } from '../../store/types/enums/WorkingMethod'
import Dictionary from '../../store/types/models/Dictionary'
import DateInput from '../../components/Form/Inputs/DateInput'
import dayjs from 'dayjs'
import { isUrl } from '../../store/utils/isUrlUtils'

type CandidateDetailsInputsProps = {
  control: Control<CandidateFormValues>
  localizations: Dictionary[] | undefined
}

const CandidateDetailsInputs = ({ control, localizations }: CandidateDetailsInputsProps) => {
  const phoneUtil = PhoneNumberUtil.getInstance()
  const workingMethodOptions: Option[] = [
    { value: WorkingMethod.STATIONARY, label: t('workingMethod.stationary') },
    { value: WorkingMethod.HYBRID, label: t('workingMethod.hybrid') },
    { value: WorkingMethod.REMOTE, label: t('workingMethod.remote') },
  ]

  const localizationOptions: Option[] =
    localizations?.map(localization => ({
      label: localization.value,
      value: localization.id,
    })) ?? []

  return (
    <>
      <TextInput
        required
        name={'firstName'}
        control={control}
        label={t('page.candidateDetails.firstName')}
        placeholder={t('page.usersProfile.form.fields.firstNamePlaceholder')}
        rules={{
          required: t('page.usersProfile.form.errors.firstNameRequired'),
          maxLength: { value: 50, message: t('page.usersProfile.form.errors.firstNameMaxLengh') },
          validate: {
            notBlank: value => {
              if (typeof value === 'string' && value.trim() === '') {
                return t('page.usersProfile.form.errors.onlyWhitespaceNotAccepted')
              }
              return true
            },
          },
          pattern: {
            value:
              /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+(?:[ ,.'-][a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+)*$/,
            message: t('page.usersProfile.form.errors.namePattern'),
          },
        }}
        maxLength={50}
      />
      <TextInput
        required
        name={'lastName'}
        control={control}
        label={t('page.candidateDetails.lastName')}
        placeholder={t('page.usersProfile.form.fields.lastNamePlaceholder')}
        rules={{
          required: t('page.usersProfile.form.errors.lastNameRequired'),
          maxLength: { value: 50, message: t('page.usersProfile.form.errors.lastNameMaxLengh') },
          validate: {
            notBlank: value => {
              if (typeof value === 'string' && value.trim() === '') {
                return t('page.usersProfile.form.errors.onlyWhitespaceNotAccepted')
              }
              return true
            },
          },
          pattern: {
            value:
              /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+(?:[ ,.'-][a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+)*$/,
            message: t('page.usersProfile.form.errors.namePattern'),
          },
        }}
        maxLength={50}
      />
      <TextInput
        name={'domicile'}
        control={control}
        label={t('page.candidateDetails.tiles.domicile')}
        placeholder={t('page.candidateDetails.form.placeholder.domicile')}
        required
        rules={{
          maxLength: { value: 50, message: t('page.candidateDetails.form.errors.domicileMaxLength') },
          required: t('page.candidateDetails.form.errors.domicileRequired'),
        }}
        maxLength={50}
      />

      <Typography variant="h5" color="text.highEmphasis">
        {t('page.candidateDetails.contactInformation')}
      </Typography>

      <TextInput
        name="email"
        control={control}
        label={t('page.usersProfile.form.fields.email')}
        placeholder={t('page.usersProfile.form.fields.emailPlaceholder')}
        rules={{
          pattern: {
            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: t('page.candidateDetails.form.errors.emailIncorrect'),
          },
        }}
      />

      <TextInput
        name="phoneNumber"
        control={control}
        label={t('page.usersProfile.form.fields.phone')}
        placeholder={t('page.usersProfile.form.fields.phonePlaceholder')}
        rules={{
          maxLength: {
            value: 12,
            message: t('page.usersProfile.form.errors.phoneLength'),
          },
          minLength: {
            value: 0,
            message: t('page.usersProfile.form.errors.phoneLength'),
          },
          validate: {
            whitespace: value => {
              if (typeof value === 'string' && /\s/.test(value)) {
                return t('page.usersProfile.form.errors.whitespaceNotAllowed')
              } else return true
            },
            valid: value => {
              if (value === null || value === '' || value === undefined) return true
              if (typeof value === 'string') {
                try {
                  const parsedPhoneNumber = phoneUtil.parse(value, 'PL')
                  return phoneUtil.isValidNumber(parsedPhoneNumber)
                    ? true
                    : t('page.usersProfile.form.errors.phoneInvalid')
                } catch (e) {
                  return t('page.usersProfile.form.errors.phoneInvalid')
                }
              }
              return t('page.usersProfile.form.errors.phoneInvalid')
            },
          },
        }}
      />

      <Typography variant="h5" color="text.highEmphasis">
        {t('page.candidateDetails.applicationInformation')}
      </Typography>

      <TextInput
        name={'cv'}
        control={control}
        required
        label={t('page.candidateDetails.tiles.cv')}
        placeholder={t('page.candidateDetails.form.placeholder.cv')}
        rules={{
          maxLength: { value: 100, message: t('page.candidateDetails.form.errors.cvMaxLength') },
          required: t('page.candidateDetails.form.errors.cvRequired'),
          validate: { isUrl },
        }}
        maxLength={100}
      />

      <DateInput
        name={'dateOfApplication'}
        control={control}
        label={t('page.candidateDetails.tiles.dateOfReceipt') + ' *'}
        rules={{
          required: t('page.candidateDetails.form.errors.dateOfReceiptRequired'),
        }}
        defaultValue={dayjs().utc(true)}
      />

      <SelectInput
        options={workingMethodOptions}
        name={'workingMethod'}
        control={control}
        label={t('page.candidateDetails.tiles.workingMethod') + ' *'}
        rules={{ required: t('page.candidateDetails.form.errors.workingMethodRequired') }}
      />

      <SelectInput
        options={localizationOptions}
        name={'localization'}
        control={control}
        label={t('page.candidateDetails.tiles.localization') + ' *'}
        placeholder={t('page.candidateDetails.form.placeholder.localization')}
        rules={{ required: t('page.candidateDetails.form.errors.localizationRequired') }}
      />

      <TextInput
        name={'repository'}
        control={control}
        label={t('page.candidateDetails.tiles.repository')}
        placeholder={t('page.candidateDetails.form.placeholder.repository')}
        rules={{
          maxLength: { value: 100, message: t('page.candidateDetails.form.errors.repositoryMaxLength') },
          validate: { isUrl },
        }}
        maxLength={100}
      />

      <TextInput
        name={'description'}
        control={control}
        label={t('page.candidateDetails.tiles.description')}
        maxLength={500}
        rules={{
          maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
        }}
        multiline
        rows={8}
        showCharCounter
      />
    </>
  )
}

export default CandidateDetailsInputs
